/**
 * Left-hand panel of TripReplayMap: ride parties, trip stats and the stops/milestones timeline.
 * Memoized — none of it depends on the playback clock, so it doesn't re-render while playing.
 */
import React, { memo } from 'react';
import { AMBER, GREEN, RED, hms, initials, pick } from './tripReplayData';

function TripReplayInfo({ ride, data, timeline, wide, onJump }) {
  const s = data ? data.summary : null;
  const plate = pick(ride, 'vehicleNo') || '—';
  const driverMobile = pick(ride, 'driverMobileNo') || '';
  const rank = pick(ride, 'crewRank'), emp = pick(ride, 'employeeNo'), pos = pick(ride, 'crewPosition');
  const crewMeta = [rank && emp ? `${rank}-${emp}` : (rank || emp), pos].filter(Boolean).join(' · ');

  return (
    <div className="ltr-left">
      <div className="ltr-card">
        <div className="ltr-sec">
          <span className="ltr-lbl">Transporter</span>
          <div className="ltr-row">
            <span className="ltr-ticon"><svg viewBox="0 0 24 24" width="12" height="12" fill="#3ee0a3"><path d="M4 21V5l8-3v19H4zm10 0V9l6 2v10h-6zM7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7z" /></svg></span>
            <span className="ltr-transporter">{pick(ride, 'transporterName') || '—'}</span>
          </div>
        </div>
        <div className="ltr-sec">
          <span className="ltr-lbl">Driver</span>
          <div className="ltr-row">
            <span className="ltr-avatar">{initials(pick(ride, 'driverName'))}</span>
            <div className="ltr-col">
              <span className="ltr-name">{pick(ride, 'driverName') || '—'}</span>
              <div className="ltr-row ltr-wrap">
                {driverMobile && <a className="ltr-mono ltr-tel" href={`tel:${driverMobile.replace(/[^\d+]/g, '')}`}>{driverMobile}</a>}
                <span className="ltr-plate ltr-mono">{plate}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="ltr-sec">
          <span className="ltr-lbl">Crew</span>
          <div className="ltr-row">
            <span className="ltr-avatar ltr-avatar-on">{initials(pick(ride, 'paxName'))}</span>
            <div className="ltr-col">
              <span className="ltr-name ltr-accent">{pick(ride, 'paxName') || '—'}</span>
              {crewMeta && <span className="ltr-sub">{crewMeta}</span>}
            </div>
          </div>
        </div>
        <div className="ltr-route">
          <span className="ltr-dot-o" /><span>{pick(ride, 'pickupLocation') || '—'}</span>
          <span className="ltr-dot-d" /><span>{pick(ride, 'dropoffLocation') || '—'}</span>
        </div>
      </div>

      <div className="ltr-card">
        <div className="ltr-stats">
          <div><div className="ltr-lbl">Distance</div><div className="ltr-val ltr-mono">{s ? `${s.km.toFixed(1)} km` : '—'}</div></div>
          <div><div className="ltr-lbl">Duration</div><div className="ltr-val ltr-mono">{s ? s.duration : '—'}</div></div>
          <div><div className="ltr-lbl">Avg moving</div><div className="ltr-val ltr-mono">{s ? `${Math.round(s.avg)} km/h` : '—'}</div></div>
          <div><div className="ltr-lbl">Max speed</div><div className="ltr-val ltr-mono">{s ? `${Math.round(s.max)} km/h` : '—'}</div></div>
        </div>
        <div className="ltr-states">
          <div><div className="ltr-lbl"><i style={{ background: GREEN }} />Moving</div><div className="ltr-mono">{s ? s.moving : '—'}</div></div>
          <div><div className="ltr-lbl"><i style={{ background: AMBER }} />Idle</div><div className="ltr-mono">{s ? s.idle : '—'}</div></div>
          <div><div className="ltr-lbl"><i style={{ background: RED }} />Stopped</div><div className="ltr-mono">{s ? s.stopped : '—'}</div></div>
        </div>
        {wide && timeline.length > 0 && (
          <>
            <div className="ltr-tlhead">
              <span className="ltr-lbl">Timeline</span>
              <span className="ltr-mono ltr-tlmeta">
                {data.stops.length} stops · {data.events.length} milestones{data.overCount ? ` · ${data.overCount} over` : ''}
              </span>
            </div>
            <div className="ltr-tl">
              {timeline.map(it => (
                <button key={`${it.kind}-${it.start}-${it.label}`} className="ltr-tlitem" onClick={() => onJump(it)}>
                  <span className="ltr-tldot" style={{ background: it.color }} />
                  <span className="ltr-mono">{it.time || hms(data.t0 + it.start)}</span>
                  <span className="ltr-mono ltr-tllabel" style={{ color: it.color }}>{it.label}</span>
                  <span className="ltr-tlsub">{it.sub}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default memo(TripReplayInfo);
